<template>
  <div class="add-contract-container">
    <!-- (1) PageHeader -->
    <ds-page-header
      :title="addContractInfo.parentContractCode ? $t('contract.renewContract') : $t('contract.draftContract')"
      :subtitle="$t('contract.page.subtitle')">
      <template #extra>
        <el-button icon="el-icon-back" @click="_goBack">
          {{ $t('common.back') }}
        </el-button>
        <el-button type="primary" icon="el-icon-check" @click="saveContractInfo">
          {{ $t('common.submit') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (2) 基本信息面板 -->
    <div class="ds-panel">
      <div class="ds-panel__header">
        <span class="ds-panel__title">{{ addContractInfo.parentContractCode ? $t('contract.renewContract') : $t('contract.draftContract') }}</span>
      </div>

      <el-form ref="form" :model="addContractInfo" label-width="150px" label-position="right" class="ds-form" :rules="rules">
        <!-- Parent Contract Info -->
        <el-row v-if="addContractInfo.parentContractCode" :gutter="16">
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item :label="$t('contract.parentContractName')">
              <el-input v-model="addContractInfo.parentContractName" disabled />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item :label="$t('contract.parentContractCode')">
              <el-input v-model="addContractInfo.parentContractCode" disabled />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item :label="$t('contract.parentContractStatus')">
              <el-input v-model="addContractInfo.parentStateName" disabled />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- Basic Contract Info -->
        <el-row :gutter="16">
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item :label="$t('contract.contractName')" prop="contractName" required>
              <el-input v-model="addContractInfo.contractName" :placeholder="$t('contract.requiredContractName')" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item :label="$t('contract.contractCode')" prop="contractCode" required>
              <el-input v-model="addContractInfo.contractCode" :placeholder="$t('contract.requiredContractCode')" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item :label="$t('contract.contractType')" prop="contractType" required>
              <el-select v-model="addContractInfo.contractType" :placeholder="$t('contract.requiredContractType')"
                class="w-full" @change="_changeContractType">
                <el-option v-for="item in addContractInfo.contractTypes" :key="item.contractTypeId" :label="item.typeName"
                  :value="item.contractTypeId" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- Party A Info -->
        <el-row :gutter="16">
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item :label="$t('contract.partyA')" prop="partyA" required>
              <el-select v-model="addContractInfo.partyA" :placeholder="$t('contract.requiredPartyA')" class="w-full"
                @change="_changeContractPartyA">
                <el-option v-for="item in addContractInfo.contractPartyAs" :key="item.partyA" :label="item.partyA"
                  :value="item.partyA" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item :label="$t('contract.partyAContact')" prop="aContacts" required>
              <el-input v-model="addContractInfo.aContacts" disabled />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item :label="$t('contract.partyAPhone')" prop="aLink" required>
              <el-input v-model="addContractInfo.aLink" disabled />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- Party B Info -->
        <el-row :gutter="16">
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item :label="$t('contract.partyB')" prop="partyB" required>
              <el-input v-model="addContractInfo.partyB" disabled style="width:70%" />
              <el-button type="primary" size="small" @click="_searchOwner">{{ $t('contract.select') }}</el-button>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item :label="$t('contract.partyBContact')" prop="bContacts" required>
              <el-input v-model="addContractInfo.bContacts" disabled />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item :label="$t('contract.partyBPhone')" prop="bLink" required>
              <el-input v-model="addContractInfo.bLink" disabled />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- Operator Info -->
        <el-row :gutter="16">
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item :label="$t('contract.operator')" prop="operator" required>
              <el-input v-model="addContractInfo.operator" :placeholder="$t('contract.requiredOperator')" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item :label="$t('contract.operatorPhone')" prop="operatorLink" required>
              <el-input v-model="addContractInfo.operatorLink" :placeholder="$t('contract.requiredOperatorPhone')" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item :label="$t('contract.contractAmount')">
              <el-input v-model="addContractInfo.amount" :placeholder="$t('contract.requiredContractAmount')" />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- Time Info -->
        <el-row :gutter="16">
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item :label="$t('contract.startTime')" prop="startTime" required>
              <el-date-picker v-model="addContractInfo.startTime" type="date" value-format="yyyy-MM-dd" class="w-full"
                :placeholder="$t('contract.requiredStartTime')" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item :label="$t('contract.endTime')" prop="endTime" required>
              <el-date-picker v-model="addContractInfo.endTime" type="date" value-format="yyyy-MM-dd" class="w-full"
                :placeholder="$t('contract.requiredEndTime')" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item :label="$t('contract.signingTime')" prop="signingTime" required>
              <el-date-picker v-model="addContractInfo.signingTime" type="datetime" value-format="yyyy-MM-dd HH:mm:ss"
                class="w-full" :placeholder="$t('contract.requiredSigningTime')" />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- Contract Specs -->
        <div v-for="(item, index) in addContractInfo.contractTypeSpecs" :key="index">
          <el-row :gutter="16" v-if="index % 3 == 0">
            <el-col :xs="24" :sm="12" :md="8" v-for="i in 3" :key="i">
              <el-form-item :label="addContractInfo.contractTypeSpecs[index + i - 1].specName"
                v-if="index + i - 1 < addContractInfo.contractTypeSpecs.length">
                <el-input v-model="addContractInfo.contractTypeSpecs[index + i - 1].value" />
              </el-form-item>
            </el-col>
          </el-row>
        </div>

        <!-- Contract Attachments -->
        <el-form-item :label="$t('contract.contractAttachments')">
          <el-button type="primary" size="mini" icon="el-icon-plus" @click="addFileStep">
            {{ $t('contract.addAttachment') }}
          </el-button>
          <div v-for="(item, index) in addContractInfo.contractFilePo" :key="index" class="ds-file-row">
            <el-row :gutter="16">
              <el-col :span="2">
                <span>{{ $t('contract.attachment') }}{{ index + 1 }}</span>
              </el-col>
              <el-col :span="10">
                <input type="file" @change="getFile($event, index)" accept=".png,.pdf,.jpg" />
              </el-col>
              <el-col :span="10">
                <span>{{ item.fileRealName }}</span>
              </el-col>
              <el-col :span="2">
                <el-button type="text" class="ds-btn-danger" @click="deleteStep(item)">{{ $t('contract.deleteAttachment') }}</el-button>
              </el-col>
            </el-row>
          </div>
        </el-form-item>
      </el-form>
    </div>

    <!-- Related Rooms -->
    <div class="ds-panel">
      <ds-toolbar :total="addContractInfo.rooms.length" :show-column-setting="false" :show-density="false" :show-refresh="false">
        <template #left>
          <span class="ds-panel__title">{{ $t('contract.relatedRooms') }}</span>
        </template>
        <template #right>
          <el-button type="primary" size="mini" icon="el-icon-plus" @click="_selectRoom">
            {{ $t('contract.add') }}
          </el-button>
        </template>
      </ds-toolbar>

      <ds-data-table :data="addContractInfo.rooms" border :empty-description="$t('contract.page.emptyRooms')">
        <el-table-column prop="floorNum" :label="$t('contract.room')" align="center" min-width="160">
          <template slot-scope="scope">
            {{ scope.row.floorNum }}-{{ scope.row.unitNum }}-{{ scope.row.roomNum }}
          </template>
        </el-table-column>
        <el-table-column prop="ownerName" :label="$t('contract.owner')" align="center" min-width="110" />
        <el-table-column prop="link" :label="$t('contract.phone')" align="center" width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.link || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="builtUpArea" :label="$t('contract.builtUpArea')" align="right" width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.builtUpArea }}{{ $t('contract.squareMeter') }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="stateName" :label="$t('contract.roomStatus')" align="center" width="120" />
        <el-table-column :label="$t('common.operation')" align="center" width="110" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" class="ds-btn-danger" @click="_openDelRoomModel(scope.row)">{{ $t('common.delete') }}</el-button>
          </template>
        </el-table-column>
      </ds-data-table>
    </div>

    <!-- Approvers -->
    <div v-show="addContractInfo.audit == '1001'" class="ds-panel ds-panel--padded">
      <purchase-approvers ref="purchaseApprovers" flow-type="50005" @notify3="handleNotify" />
    </div>

    <!-- Buttons -->
    <div class="ds-form-actions">
      <el-button @click="_goBack">{{ $t('common.back') }}</el-button>
      <el-button type="primary" icon="el-icon-check" @click="saveContractInfo">{{ $t('common.submit') }}</el-button>
    </div>

    <!-- Modals -->
    <search-room ref="searchRoom" @chooseRoom="handleChooseRoom" />
    <search-owner ref="searchOwner" @chooseOwner="handleChooseOwner" />
  </div>
</template>

<script>
import { getCommunityId } from '@/api/community/communityApi'
import { saveContract, queryContractType, queryContractPartya, queryContractTypeSpec, queryContractRoom } from '@/api/contract/addContractApi'
import PurchaseApprovers from '@/components/contract/purchaseApprovers'
import SearchRoom from '@/components/room/searchRoom'
import SearchOwner from '@/components/owner/SearchOwner'
import { uploadFile } from '@/api/system/paymentPoolApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'

export default {
  name: 'AddContractList',
  components: {
    PurchaseApprovers,
    SearchRoom,
    SearchOwner,
    DsPageHeader,
    DsToolbar,
    DsDataTable
  },
  data() {
    return {
      addContractInfo: {
        contractId: '',
        contractName: '',
        contractCode: '',
        contractType: '',
        partyA: '',
        partyB: '',
        aContacts: '',
        bContacts: '',
        aLink: '',
        bLink: '',
        operator: '',
        operatorLink: '',
        amount: '',
        startTime: '',
        endTime: '',
        signingTime: '',
        contractTypes: [],
        contractTypeSpecs: [],
        objType: '1111',
        objId: '-1',
        contractParentId: '',
        parentContractCode: '',
        parentContractName: '',
        parentStateName: '',
        rooms: [],
        contractFilePo: [],
        tempfile: '',
        contractPartyAs: [],
        audit: '',
        staffName: '',
        nextUserId: ''
      },
      communityId: '',
      rules: {
        contractName: [{ required: true, message: this.$t('contract.requiredContractName'), trigger: 'blur' }],
        contractCode: [{ required: true, message: this.$t('contract.requiredContractCode'), trigger: 'blur' }],
        contractType: [{ required: true, message: this.$t('contract.requiredContractType'), trigger: 'blur' }],
        operator: [{ required: true, message: this.$t('contract.requiredOperator'), trigger: 'blur' }],
        operatorLink: [{ required: true, message: this.$t('contract.requiredOperatorPhone'), trigger: 'blur' }],
        amount: [{ required: true, message: this.$t('contract.requiredContractAmount'), trigger: 'blur' }],
        startTime: [{ required: true, message: this.$t('contract.requiredStartTime'), trigger: 'blur' }],
        endTime: [{ required: true, message: this.$t('contract.requiredEndTime'), trigger: 'blur' }],
        signingTime: [{ required: true, message: this.$t('contract.requiredSigningTime'), trigger: 'blur' }],
        partyA: [{ required: true, message: this.$t('contract.requiredPartyA'), trigger: 'blur' }],
      }
    }
  },
  created() {
    this.communityId = getCommunityId()
    this._loadAddContractType()
    this._loadAddContractParkA()
    if (this.$route.query.contractId) {
      this.addContractInfo.contractParentId = this.$route.query.contractId
      this.addContractInfo.parentContractCode = this.$route.query.contractCode
      this.addContractInfo.parentContractName = this.$route.query.contractName
      this.addContractInfo.parentStateName = this.$route.query.stateName
      this._queryRoom()
      this._listContracts()
    }
  },
  methods: {
    _loadAddContractType() {
      queryContractType({ page: 1, row: 100 }).then(response => {
        this.addContractInfo.contractTypes = response.data
      })
    },
    _loadAddContractParkA() {
      queryContractPartya({ page: 1, row: 100 }).then(response => {
        this.addContractInfo.contractPartyAs = response.data
      })
    },
    _changeContractType() {
      this.addContractInfo.contractTypes.forEach(item => {
        if (this.addContractInfo.contractType == item.contractTypeId) {
          this.addContractInfo.audit = item.audit
        }
      })

      this.addContractInfo.contractTypeSpecs = []
      queryContractTypeSpec({
        page: 1,
        row: 100,
        contractTypeId: this.addContractInfo.contractType
      }).then(response => {
        response.data.forEach(item => {
          item.value = ''
          if (item.specShow == 'Y') {
            item.values = []
            this.addContractInfo.contractTypeSpecs.push(item)
          }
        })
      })
    },
    _queryRoom() {
      queryContractRoom({
        contractId: this.$route.query.contractId,
        page: 1,
        row: 100
      }).then(response => {
        this.addContractInfo.rooms = response.data
      })
    },
    _selectRoom() {
      this.$refs.searchRoom.open()
    },
    _searchOwner() {
      this.$refs.searchOwner.open()
    },
    handleChooseRoom(room) {
      this.addContractInfo.rooms.push(room)
    },
    handleChooseOwner(owner) {
      this.addContractInfo.partyB = owner.name
      this.addContractInfo.bContacts = owner.name
      this.addContractInfo.bLink = owner.link
      this.addContractInfo.objId = owner.ownerId
    },
    handleNotify(info) {
      this.addContractInfo.nextUserId = info.staffId
      this.addContractInfo.staffName = info.staffName
    },
    _openDelRoomModel(room) {
      this.addContractInfo.rooms = this.addContractInfo.rooms.filter(item => item.roomId != room.roomId)
    },
    addFileStep() {
      this.addContractInfo.contractFilePo.push({
        seq: this.addContractInfo.contractFilePo.length,
        fileSaveName: '',
        fileRealName: ''
      })
    },
    deleteStep(step) {
      this.addContractInfo.contractFilePo = this.addContractInfo.contractFilePo.filter(item => item.seq != step.seq)
    },
    getFile(e, index) {
      this.addContractInfo.tempfile = e.target.files[0]
      this.addContractInfo.contractFilePo[index].fileRealName = this.addContractInfo.tempfile.name
      this._importData(index)
    },
    async _importData(index) {
      console.log(index)
      const _fileName = this.addContractInfo.tempfile.name
      const _suffix = _fileName.substring(_fileName.lastIndexOf('.') + 1)
      if (!this.checkFileType(_suffix.toLowerCase())) {
        this.$message.error(this.$t('contract.invalidFileType'))
        return
      }

      const formData = new FormData()
      formData.append("uploadFile", this.addContractInfo.tempfile)
      const data = await uploadFile(formData, {})
      this.addContractInfo.contractFilePo[index].fileSaveName = data.realFileName
    },
    checkFileType(fileType) {
      return ['png', 'pdf', 'jpg'].includes(fileType)
    },
    _changeContractPartyA() {
      const partyA = this.addContractInfo.partyA
      this.addContractInfo.contractPartyAs.forEach(item => {
        if (partyA == item.partyA) {
          this.addContractInfo.aLink = item.aLink
          this.addContractInfo.aContacts = item.aContacts
        }
      })
    },
    _listContracts() {
    },
    saveContractInfo() {
      if (!this.validateForm()) {
        return
      }

      this.addContractInfo.communityId = this.communityId
      saveContract(this.addContractInfo).then(response => {
        if (response.code === 0) {
          this.$message.success(this.$t('common.operationSuccess'))
          this._goBack()
        } else {
          this.$message.error(response.msg)
        }
      })
    },
    validateForm() {
      return true
    },
    _goBack() {
      this.$router.go(-1)
    }
  }
}
</script>

<style lang="scss" scoped>
.add-contract-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-md);

  &__header {
    padding: var(--ds-spacing-md) var(--ds-spacing-lg);
    border-bottom: 1px solid var(--ds-gray-200);
  }

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }

  &--padded {
    padding: var(--ds-spacing-lg);
  }
}

.ds-form {
  padding: var(--ds-spacing-lg) var(--ds-spacing-lg) 0;

  ::v-deep .el-form-item {
    margin-bottom: var(--ds-spacing-lg);
  }

  ::v-deep .el-form-item__label {
    color: var(--ds-gray-700);
    font-weight: 500;
  }
}

.ds-file-row {
  margin-top: var(--ds-spacing-sm);
  padding-top: var(--ds-spacing-sm);
  border-top: 1px dashed var(--ds-gray-200);
}

.ds-form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--ds-spacing-sm);
  padding: var(--ds-spacing-md) 0;
}

.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
